import { computed, onBeforeUnmount, onMounted, ref, type ComputedRef } from 'vue';
import type { NxConfirmChoice, NxConfirmTone } from './types';

/** A confirm request waiting in the queue, as `NxConfirmHost` renders it. */
export interface NxConfirmRequest {
  kind: 'confirm';
  id: number;
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  alternativeLabel?: string;
  tone: NxConfirmTone;
  resolve: (choice: NxConfirmChoice) => void;
}

/** A prompt request waiting in the queue, as `NxConfirmHost` renders it. */
export interface NxPromptRequest {
  kind: 'prompt';
  id: number;
  title: string;
  message?: string;
  label?: string;
  value: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  required: boolean;
  validate?: (value: string) => string | null | undefined;
  resolve: (value: string | null) => void;
}

export type NxDialogRequest = NxConfirmRequest | NxPromptRequest;

// Module state, like the toast list: one queue for the whole app, shown one
// dialog at a time by the most recently mounted host.
const queue = ref<NxDialogRequest[]>([]);
const hostStack = ref<symbol[]>([]);
let seq = 0;

/** The request on screen (the head of the queue), `null` when idle. */
export const currentDialogRequest = computed(() => queue.value[0] ?? null);

/** Whether any `NxConfirmHost` is mounted — without one, the APIs fall back to the browser. */
export function hasDialogHost(): boolean {
  return hostStack.value.length > 0;
}

export function nextDialogId(): number {
  return ++seq;
}

export function enqueueDialog(request: NxDialogRequest): void {
  queue.value = [...queue.value, request];
}

/** Takes a request off the queue; the next one (if any) shows after it. */
export function settleDialog(id: number): void {
  queue.value = queue.value.filter((r) => r.id !== id);
}

/**
 * Host coordination, as for toasts: with nested hosts only the MOST RECENTLY
 * mounted one renders the queue, so a dialog never shows twice.
 */
export function useNxConfirmHostPresence(): ComputedRef<boolean> {
  const token = Symbol('nx-confirm-host');
  onMounted(() => {
    hostStack.value = [...hostStack.value, token];
  });
  onBeforeUnmount(() => {
    hostStack.value = hostStack.value.filter((t) => t !== token);
    // The last host is gone (route change, teardown): nothing can show the
    // queue any more, so settle it as cancelled instead of leaving callers
    // awaiting forever.
    if (!hostStack.value.length) cancelPending();
  });
  return computed(() => hostStack.value.at(-1) === token);
}

/** Resolves every queued request as cancelled and empties the queue. */
function cancelPending(): void {
  const pending = queue.value;
  queue.value = [];
  for (const request of pending) {
    if (request.kind === 'confirm') {
      request.resolve('cancel');
    } else {
      request.resolve(null);
    }
  }
}

/**
 * Clears the queue and the host stack (tests / HMR teardown). Pending
 * requests resolve as cancelled, so no caller is left awaiting forever.
 */
export function resetNxConfirmQueue(): void {
  cancelPending();
  hostStack.value = [];
}

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    resetNxConfirmQueue();
  });
}
